import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

type TabType = 'onetime' | 'monthly';

interface Package {
  id: string;
  name: string;
  price: string;
  priceNote: string;
  description: string;
  icon: string;
  popular: boolean;
  badge?: string;
}

const ONE_TIME_PACKAGES: Package[] = [
  {
    id: 'starter',
    name: 'Starter Website',
    price: '$500',
    priceNote: 'one-time',
    description: '1-page website, mobile friendly, contact form, social media setup & 1 platform managed',
    icon: 'ri-rocket-line',
    popular: false,
  },
  {
    id: 'business',
    name: 'Business Website',
    price: '$680',
    priceNote: 'one-time',
    description: 'Multi-section website, contact form, 2 platforms managed (FB + IG), weekly content posting',
    icon: 'ri-briefcase-line',
    popular: false,
  },
  {
    id: 'growth',
    name: 'Growth Package',
    price: '$1,000',
    priceNote: 'one-time',
    description: 'Full website build, Google Business profile, full social media management, daily posting & ad support',
    icon: 'ri-line-chart-line',
    popular: true,
  },
  {
    id: 'google_business',
    name: 'Google Business Setup',
    price: '$100',
    priceNote: 'one-time',
    description: 'Helps your business appear on Google searches and Maps immediately',
    icon: 'ri-google-line',
    popular: false,
  },
];

const MONTHLY_PACKAGES: Package[] = [
  {
    id: 'maintenance',
    name: 'Website Maintenance',
    price: '$100',
    priceNote: '/month',
    description: 'Keep your website updated, secure and running smoothly — cancel anytime',
    icon: 'ri-settings-3-line',
    popular: false,
    badge: 'Add-on',
  },
  {
    id: 'social_basic',
    name: 'Social Media Starter',
    price: '$500',
    priceNote: '/month',
    description: '1 platform managed (Facebook or Instagram), 3x weekly posts, basic engagement monitoring',
    icon: 'ri-smartphone-line',
    popular: false,
  },
  {
    id: 'social_pro',
    name: 'Social Media Pro',
    price: '$1,000',
    priceNote: '/month',
    description: '2 platforms managed (FB + IG), daily posting, story creation, engagement monitoring & monthly reporting',
    icon: 'ri-bar-chart-2-line',
    popular: true,
  },
  {
    id: 'google_ads_starter',
    name: 'Google Ads Starter',
    price: '$500',
    priceNote: '/month',
    description: 'Google Ads campaign setup & management, keyword targeting, monthly performance report — great for local businesses',
    icon: 'ri-google-line',
    popular: false,
  },
  {
    id: 'google_ads_vip',
    name: 'Google Ads VIP',
    price: '$1,500',
    priceNote: '/month',
    description: 'Full Google Ads management, advanced targeting, A/B ad testing, remarketing, dedicated account manager & bi-weekly reporting',
    icon: 'ri-vip-crown-line',
    popular: false,
    badge: 'VIP',
  },
  {
    id: 'full_digital',
    name: 'Full Digital Retainer',
    price: '$2,000',
    priceNote: '/month',
    description: 'Everything — website maintenance, full social media management, Google Ads & monthly performance reporting',
    icon: 'ri-award-line',
    popular: false,
    badge: 'Best Value',
  },
];

const MONTHLY_ID_SET = new Set(MONTHLY_PACKAGES.map((p) => p.id));

export default function PayPage() {
  const [searchParams] = useSearchParams();
  const initPkg = searchParams.get('pkg') || null;
  const initTab: TabType = initPkg && MONTHLY_ID_SET.has(initPkg) ? 'monthly' : 'onetime';

  const [tab, setTab] = useState<TabType>(initTab);
  const [selected, setSelected] = useState<string | null>(initPkg);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const activePackages = tab === 'onetime' ? ONE_TIME_PACKAGES : MONTHLY_PACKAGES;

  const handleTabChange = (newTab: TabType) => {
    setTab(newTab);
    setSelected(null);
    setError('');
  };

  const handlePay = async () => {
    if (!selected) { setError('Please select a package to continue.'); return; }
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return; }
    setError('');
    setLoading(true);

    try {
      const origin = window.location.origin;
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId: selected,
          customerName: name.trim(),
          customerEmail: email.trim(),
          successUrl: `${origin}/pay/success?pkg=${selected}`,
          cancelUrl: `${origin}/pay`,
        }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to connect to payment processor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPkg = activePackages.find((p) => p.id === selected);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Nav */}
      <header className="border-b border-white/10 px-5 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-flashlight-fill text-orange-400 text-xl" />
          </div>
          <span className="text-white font-black text-base tracking-widest uppercase">Nova AI</span>
        </Link>
        <Link to="/#pricing" className="text-white/40 text-xs tracking-widest uppercase hover:text-white transition-colors cursor-pointer whitespace-nowrap">
          View Pricing
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-12 py-14 md:py-20">

        {/* Header */}
        <div
          className="mb-10 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span className="text-white/40 text-xs tracking-[0.25em] uppercase font-medium">Secure Checkout</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mt-3 leading-none tracking-tight">
            Pay for Your<br />
            <span className="text-transparent" style={{ WebkitTextStroke: '1.5px rgba(255,200,0,0.8)' }}>
              Package
            </span>
          </h1>
          <p className="text-white/40 text-sm mt-4 max-w-md leading-relaxed">
            Select your package and complete payment securely via Stripe. You&apos;ll receive a confirmation email right after.
          </p>
        </div>

        {/* Tab switcher */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '60ms' }}
        >
          <div className="inline-flex border border-white/15 p-1 rounded-full">
            <button
              onClick={() => handleTabChange('onetime')}
              className={`px-5 py-2 text-xs font-bold tracking-widest uppercase rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === 'onetime'
                  ? 'bg-white text-black'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              One-Time
            </button>
            <button
              onClick={() => handleTabChange('monthly')}
              className={`px-5 py-2 text-xs font-bold tracking-widest uppercase rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === 'monthly'
                  ? 'bg-white text-black'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              Monthly Plans
            </button>
          </div>

          {tab === 'monthly' && (
            <div className="mt-3 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse flex-shrink-0" />
              <p className="text-orange-400/80 text-xs tracking-wide">Billed monthly — cancel anytime, no contracts</p>
            </div>
          )}
        </div>

        {/* Package selector */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '120ms' }}
        >
          <p className="text-white/40 text-xs tracking-widest uppercase mb-4">1. Choose Your Package</p>
          <div className="flex flex-col gap-2">
            {activePackages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelected(pkg.id)}
                className={`w-full flex items-center gap-4 p-4 border text-left transition-all duration-200 cursor-pointer group ${
                  selected === pkg.id
                    ? 'border-orange-400 bg-orange-400/5'
                    : 'border-white/10 hover:border-white/30 bg-transparent'
                }`}
              >
                {/* Radio */}
                <div
                  className={`w-5 h-5 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${
                    selected === pkg.id ? 'border-orange-400' : 'border-white/30'
                  }`}
                >
                  {selected === pkg.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-400" />
                  )}
                </div>

                {/* Icon */}
                <div
                  className={`w-9 h-9 flex items-center justify-center border flex-shrink-0 transition-colors duration-200 ${
                    selected === pkg.id ? 'border-orange-400/50' : 'border-white/20 group-hover:border-white/40'
                  }`}
                >
                  <i className={`${pkg.icon} text-base ${selected === pkg.id ? 'text-orange-400' : 'text-white/50'}`} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-bold ${selected === pkg.id ? 'text-white' : 'text-white/70'}`}>
                      {pkg.name}
                    </span>
                    {pkg.popular && (
                      <span className="text-[10px] font-bold tracking-widest uppercase text-orange-400 border border-orange-400/50 px-1.5 py-0.5 whitespace-nowrap">
                        Popular
                      </span>
                    )}
                    {pkg.badge && !pkg.popular && (
                      <span className="text-[10px] font-bold tracking-widest uppercase text-white/40 border border-white/20 px-1.5 py-0.5 whitespace-nowrap">
                        {pkg.badge}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 leading-snug ${selected === pkg.id ? 'text-white/50' : 'text-white/30'}`}>
                    {pkg.description}
                  </p>
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0">
                  <span className={`text-xl font-black ${selected === pkg.id ? 'text-white' : 'text-white/60'}`}>
                    {pkg.price}
                  </span>
                  <span className="text-white/30 text-xs ml-1">{pkg.priceNote}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Details form */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '200ms' }}
        >
          <p className="text-white/40 text-xs tracking-widest uppercase mb-4">2. Your Details</p>
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase block mb-1.5">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-orange-400 transition-colors placeholder-white/20"
              />
            </div>
            <div>
              <label className="text-white/40 text-xs tracking-widest uppercase block mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-orange-400 transition-colors placeholder-white/20"
              />
            </div>
          </div>
        </div>

        {/* Order summary */}
        {selectedPkg && (
          <div className="mb-6 border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 flex items-center justify-center border border-white/20 flex-shrink-0">
                <i className={`${selectedPkg.icon} text-white/50 text-sm`} />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold truncate">{selectedPkg.name}</p>
                <p className="text-white/30 text-xs">{tab === 'monthly' ? 'Recurring subscription' : 'One-time payment'}</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white font-black text-lg">{selectedPkg.price}</p>
              <p className="text-white/30 text-xs">{selectedPkg.priceNote}</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 flex items-center gap-2.5 border border-red-500/40 bg-red-500/5 px-4 py-3">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-red-400 text-sm" />
            </div>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Pay button */}
        <div
          className="transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '300ms' }}
        >
          <button
            onClick={handlePay}
            disabled={loading}
            className="w-full py-4 bg-white text-black text-sm font-black tracking-widest uppercase hover:bg-orange-400 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-sm animate-spin" />
                </div>
                Redirecting to Stripe...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-secure-payment-line text-sm" />
                </div>
                {tab === 'monthly' ? 'Start Subscription' : 'Proceed to Secure Payment'}
              </span>
            )}
          </button>
          <p className="text-white/20 text-xs text-center mt-3">
            {tab === 'monthly'
              ? 'Subscription managed via Stripe — cancel anytime from your email receipt'
              : 'Powered by Stripe — your payment info is never stored on our servers'}
          </p>
        </div>

        {/* Trust badges */}
        <div
          className="mt-10 border-t border-white/10 pt-8 flex items-center justify-center gap-8 flex-wrap transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '450ms' }}
        >
          {[
            { icon: 'ri-shield-check-line', label: 'SSL Encrypted' },
            { icon: 'ri-lock-2-line', label: 'Secure Checkout' },
            { icon: 'ri-bank-card-line', label: 'All Major Cards' },
            { icon: 'ri-loop-left-line', label: 'Cancel Anytime' },
          ].map((badge) => (
            <div key={badge.label} className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${badge.icon} text-white/30 text-sm`} />
              </div>
              <span className="text-white/30 text-xs">{badge.label}</span>
            </div>
          ))}
        </div>

      </main>
    </div>
  );
}
